import React, {useCallback, useEffect, useState} from 'react';
import {Link, useNavigate, useSearchParams} from 'react-router-dom';
import {useAppDispatch, useAppSelector} from '../store/hooks';
import {abandonSignup, pollSignupStatus, resendSignupLink, startSignup} from '../store/slices/signupThunks';
import {checkoutCompleted} from '../store/slices/signupSlice';
import {fetchPaddleProducts} from '../store/slices/subscriptionThunks';
import {
  resendSignupLinkApiSelector,
  signupDataSelector,
  signupLinkSentSelector,
  signupPhaseSelector,
  startSignupApiSelector,
} from '../store/selectors/signup.selectors';
import {productsApiSelector, productsSelector} from '../store/selectors/subscription.selectors';
import {authLoadingSelector, isAuthenticatedSelector} from '../store/selectors/auth.selectors';
import {initializePaddle, openSignupCheckout, subscribeToPaddleEvents} from '../lib/paddle';
import AuthCard from './auth/AuthCard';
import SignupStartForm, {SignupStartValues} from './signup/SignupStartForm';
import SignupCheckInbox from './signup/SignupCheckInbox';
import {primaryButtonClass, secondaryButtonClass} from './formStyles';
import type {BillingCycleName} from '../domain/types';

/** Matches the server's one-send-per-minute throttle, so the button says no before the API has to. */
const RESEND_COOLDOWN_SECONDS = 60;
const POLL_INTERVAL_MS = 2000;
/** Past this with still no link sent, offer support — see `SignupCheckInbox`. */
const STALLED_AFTER_SECONDS = 300;

const SignInLink = () => (
  <Link to="/login" className="text-xs text-gray-600 hover:text-gray-400 transition-colors">
        Already have an account? Sign in
  </Link>
);

/**
 * Signup, which is a payment flow before it is an auth flow.
 *
 * Card first, account second: `/signup/start` parks the email and mints a token, Paddle Checkout
 * collects the card for the trial, and the subscription webhook is what actually creates the
 * account. `supabase.auth.signUp()` is refused at the auth layer, so this is the only door — there
 * is no free tier to land in.
 *
 * Four screens, one route, phase derived from the slice (`signupPhaseSelector`). The token is
 * persisted, so a reload mid-signup resumes instead of stranding someone who has already paid.
 *
 * **Paying is the end of it.** `checkout.completed` goes straight to "check your email" — the
 * account may still be a webhook away, but that is our queue, not the visitor's, and there is
 * nothing for them to do about it either way. The status poll keeps running quietly underneath so
 * the resend button can appear once there is genuinely something to resend.
 */
const SignupContainer: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const authLoading = useAppSelector(authLoadingSelector);
  const isAuthenticated = useAppSelector(isAuthenticatedSelector);
  const products = useAppSelector(productsSelector);
  const productsApi = useAppSelector(productsApiSelector);
  const data = useAppSelector(signupDataSelector);
  const phase = useAppSelector(signupPhaseSelector);
  const startApi = useAppSelector(startSignupApiSelector);
  const resendApi = useAppSelector(resendSignupLinkApiSelector);
  const linkSent = useAppSelector(signupLinkSentSelector);

  // The plan the visitor clicked on the pricing section, if they came from there.
  const planFromUrl = searchParams.get('plan') === 'yearly' ? 'yearly' : 'monthly';
  const [billingCycle, setBillingCycle] = useState<BillingCycleName>(planFromUrl);

  const [checkoutError, setCheckoutError] = useState<string | null>(null);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [cooldownSeconds, setCooldownSeconds] = useState(0);
  const [resent, setResent] = useState(false);

  // A signed-in visitor has no business here; existing accounts subscribe through /checkout.
  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      navigate('/welcome', {replace: true});
    }
  }, [authLoading, isAuthenticated, navigate]);

  useEffect(() => {
    if (productsApi.status === 'idle') {
      dispatch(fetchPaddleProducts());
    }
  }, [dispatch, productsApi.status]);

  // Paddle's event callback belongs to PaddleInitializer, which fans events out to whoever asks.
  useEffect(() => subscribeToPaddleEvents((event) => {
    if (event.name === 'checkout.completed') {
      dispatch(checkoutCompleted());
    }
  }), [dispatch]);

  // Poll only once the card has gone through: before that `pending` just means "not started", and
  // polling it would say nothing.
  useEffect(() => {
    const token = data.token;
    if (!token || !data.paid || data.status === 'provisioned' || data.status === 'expired') {
      return;
    }

    dispatch(pollSignupStatus(token));
    const interval = setInterval(() => dispatch(pollSignupStatus(token)), POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [dispatch, data.token, data.paid, data.status]);

  // Nothing on the screen counts this down — it exists only so a signup the webhook and the cron
  // both lost can be offered support instead of a resend button that will never appear.
  useEffect(() => {
    if (phase !== 'ready' || linkSent) {
      setElapsedSeconds(0);
      return;
    }
    const interval = setInterval(() => setElapsedSeconds(seconds => seconds + 1), 1000);
    return () => clearInterval(interval);
  }, [phase, linkSent]);

  useEffect(() => {
    if (cooldownSeconds <= 0) {
      return;
    }
    const timer = setTimeout(() => setCooldownSeconds(cooldownSeconds - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldownSeconds]);

  const openCheckout = useCallback(async (token: string, email: string, cycle: BillingCycleName) => {
    setCheckoutError(null);

    if (!products) {
      setCheckoutError('Pricing is still loading. Try again in a moment.');
      return;
    }

    const paddle = await initializePaddle();
    if (!paddle) {
      setCheckoutError('Checkout is unavailable right now. Please try again later.');
      return;
    }

    openSignupCheckout(paddle, {priceId: products[cycle].priceId, email, signupToken: token});
  }, [products]);

  const handleStart = async (values: SignupStartValues) => {
    setBillingCycle(values.billingCycle);

    try {
      const started = await dispatch(startSignup({email: values.email, name: values.name})).unwrap();
      await openCheckout(started.token, started.email, values.billingCycle);
    } catch {
      // The slice holds the message; `account_exists` becomes a link to sign in below.
    }
  };

  const handleResend = async () => {
    if (!data.token) {
      return;
    }

    try {
      await dispatch(resendSignupLink(data.token)).unwrap();
      setResent(true);
      setCooldownSeconds(RESEND_COOLDOWN_SECONDS);
    } catch {
      // A 429 carries how long is left, so the button can count it down instead of guessing.
      setCooldownSeconds(data.resendRetryAfterSeconds ?? RESEND_COOLDOWN_SECONDS);
    }
  };

  if (phase === 'ready') {
    return (
      <AuthCard
        title="Check your email"
        subtitle="You’re all set — the last step is in your inbox."
        footer={<SignInLink />}
      >
        <SignupCheckInbox
          email={data.email}
          linkSent={linkSent}
          stalled={elapsedSeconds >= STALLED_AFTER_SECONDS}
          resending={resendApi.status === 'pending'}
          cooldownSeconds={cooldownSeconds}
          resendError={cooldownSeconds > 0 ? null : resendApi.error}
          resent={resent}
          onResend={handleResend}
        />
      </AuthCard>
    );
  }

  if (phase === 'expired') {
    return (
      <AuthCard
        title="This signup expired"
        subtitle="Nobody completed checkout in time, and nothing was charged."
        footer={<SignInLink />}
      >
        <button
          type="button"
          onClick={() => dispatch(abandonSignup())}
          className={`mt-5 w-full ${primaryButtonClass}`}
        >
                    Start again
        </button>
      </AuthCard>
    );
  }

  if (phase === 'awaitingPayment') {
    return (
      <AuthCard
        title="Finish your trial setup"
        subtitle={`We have your details for ${data.email}. Nothing is charged until checkout completes.`}
        error={checkoutError}
        footer={<SignInLink />}
      >
        <button
          type="button"
          onClick={() => openCheckout(data.token!, data.email, billingCycle)}
          className={`mt-5 w-full ${primaryButtonClass}`}
        >
                    Reopen checkout
        </button>
        <button
          type="button"
          onClick={() => dispatch(abandonSignup())}
          className={`mt-2 w-full ${secondaryButtonClass}`}
        >
                    Start over
        </button>
      </AuthCard>
    );
  }

  return (
    <AuthCard
      title="Start your CensorMe trial"
      subtitle="Transcribe an episode and cut every word on your list."
      error={data.accountExists ? null : (checkoutError || startApi.error || productsApi.error)}
      footer={<SignInLink />}
    >
      {data.accountExists && (
        <div className="mt-4 rounded-lg border border-amber-500/30 bg-amber-500/[0.06] px-3 py-2.5
         text-sm text-amber-200">
                    That email already has a CensorMe account.{' '}
          <Link to="/login" className="font-medium underline hover:text-amber-100">Sign in instead</Link>.
        </div>
      )}

      {products ? (
        <SignupStartForm
          products={products}
          initialEmail={data.email}
          initialName={data.name}
          initialBillingCycle={billingCycle}
          submitting={startApi.status === 'pending'}
          onSubmit={handleStart}
        />
      ) : (
        <p className="mt-5 text-sm text-gray-500">
          {productsApi.status === 'rejected'
            ? 'Pricing could not be loaded, so checkout cannot start. Please refresh and try again.'
            : 'Loading pricing…'}
        </p>
      )}
    </AuthCard>
  );
};

export default SignupContainer;
